import { Fetcher } from '@ajaibid/utils';

import { defineHeaders } from './headers';
import { logFetchError } from './logger';
import waitRefreshToken from './session/waitRefreshToken';
import { refreshPinTokenFn } from './session/refreshPintoken';
import clearSession from './session/clearSession';
import { getPinToken } from './token';

const hasValidToken = (token) =>
  token != null && String(token).trim() !== '' && String(token) !== 'undefined';

/**
 * @typedef {import('@ajaibid/utils/dist/utils/fetcher/Fetcher').beforeFetcherInterceptor} beforeFetcherInterceptor
 * @typedef {import('@ajaibid/utils/dist/utils/fetcher/Fetcher').afterFetcherInterceptor} afterFetcherInterceptor
 */

export default function makeFetcher() {
  /**
   * @type {beforeFetcherInterceptor}
   */
  const beforeInterceptor = async (getConfig) => {
    const config = getConfig();
    if (config?.meta?.isUsingPinToken) {
      const pinToken = getPinToken();
      if (!hasValidToken(pinToken)) {
        clearSession();
        throw new Error('Session expired');
      }
      config.headers.Authorization = `jwt ${pinToken}`;
    }
    if (!config?.meta?.isRefreshToken) {
      await waitRefreshToken();
    }

    window.pendingFetch += 1;

    return config;
  };

  /**
   * @type {afterFetcherInterceptor}
   */
  const afterInterceptor = async (err, result) => {
    window.pendingFetch -= 1;

    if (err?.response?.status === 403) {
      clearSession();
      throw err;
    }

    if (err?.response?.status === 401) {
      await refreshPinTokenFn();
    }

    if (err) {
      logFetchError(err);
      throw err;
    }

    return result;
  };

  return Fetcher({
    headers: defineHeaders,
    baseURL: process.env.API_URL,
    interceptor: { before: beforeInterceptor, after: afterInterceptor },
  });
}
